import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TableColumn, TableSettings} from "../../../../tableSettings";
import AbstractListView, {
  DataFetchResult, ExportRowRenderer, ListCustomFilterRenderer, ListOrderBy, ListRowRenderer
} from "../../../common/AbstractListView";

import periodCloseIcon2 from '../../../../img/periodclose-12.svg';
import constants from '../../../../constants';
import {Search} from "../../../../filtering";

import {NavigationType, openNewPage} from '../../../../util/navigationHelpers';

import {
  DateRenderer, DateTimeRenderer, FloatRenderer,
  NullableCheckboxIconRenderer, TextAlignLeftRenderer, TextRenderer
} from '../../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../../uiControls/UniqueLinkButton';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../../common/JobListViewContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../../ui/ActionButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import {JobNames} from "../../../../framework.core/JobNames";
import {advanceSalesList} from "../../tableSettings";
import ListViewShortcutMenu from '../../../common/ListViewShortcutMenu';
import * as XCol from '../../../../framework.core/XExportColumn';
import ToolboxMenuItem from '../../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../../ui/menu/Menu';
import JobListSuntekCommand from '../../../common/JobListSuntekCommand';
import { FmsSecTask } from '../../../../fms.core/constants';
import { SuntekSettings, SystemSettings } from '../../../../ifmsTypes';
import { findSecurityTaskWithBranch, getSecBySecTask } from '../../../../util';
import { useJournal } from '../journal/JournalListView';
import { StatusNotUseFileLockedRenderer } from '../../../common/commonListRenderers';
import {  DeleteIconButton2, EditIconButton2, JournalIconButton, ViewIconButton, ViewIconButton2 } from '../../../../ui/IconButton';
import { SecType } from '../../../../fms.enum';
import { AdvanceSalesListItem } from '../../../../fms.data/models/AdvanceSalesListItem';

function IndicatorRenderer({ data, row}:any) {
  const l = data[row.index];
  let dataTooltip = '';
  const lStatus = (l as any).status as string;
  if (lStatus === 'PC') dataTooltip = 'Period Close';

  let content: React.ReactNode = <></>;
  if (lStatus === 'PC')
    content = <img height="12px" src={periodCloseIcon2} id={'redIcon'} style={{ position: 'relative', top: '0px', marginTop: '4px' }} alt="" />;

  return (
    <div className="ifms-table-default" id={`ifms-table-default-${row.index}`} data-tooltip={dataTooltip} style={{ width: '100%', minHeight: '22px' }}>{content}</div>
  );
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();
  const operation = 'accounting';

  const selectedElement = jobListContext.selectedElement as AdvanceSalesListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acAdvanceSales/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, `/${operation}/acAdvanceSales/add`, NavigationType.newPage);
  }

  async function deleteEntity() {
    if (!selectedElement) {
      await showGlobalOkDialog('Cannot delete!',false,null,'warning');
      return;
    }

    if (
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acAdvanceSales/edit/${selectedElement.id}`) || 
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acAdvanceSales/view/${selectedElement.id}`)
    ) {
      await showGlobalOkDialog('Need to close Prepaid Expense Entry first', false, null, "warning");
      return;
    }
  
    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    ,false, 0, true, 'warning');

    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.AdvanceSales, {
        id: selectedElement.id,
        pimsLinkId: 0
      });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message, false,null,'warning');
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/acAdvanceSales/edit/${id}`;
    const viewUrl = `/${operation}/acAdvanceSales/view/${id}`;
    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe(`/${operation}/acAdvanceSales/edit`, editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;

    const editUrl = `/${operation}/acAdvanceSales/edit/${id}`;
    const viewUrl = `/${operation}/acAdvanceSales/view/${id}`;


    const urls = [viewUrl];
    urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acAdvanceSales/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    const id = selectedElement.id;
    openNewPage(history, `/${operation}/acAdvanceSales/add?copyId=${id}`, NavigationType.newPage);
  }

  return {create, deleteEntity, edit, view, copy, operation};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteEntity, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as AdvanceSalesListItem;
  const appContext = useContext(ApplicationContext);

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, },
    { label: 'View', shortcut: 'F9', separator: false},
    { label: 'Edit', shortcut: 'F3', separator: false },
    { label: 'Delete', shortcut: '', separator: true,},
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true, newCol: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="acAdvanceSales-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: false
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        deleteEntity();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
        break;
      case 6:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}


function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const selectedElement = jobListContext.selectedElement as AdvanceSalesListItem;

  const {edit, view, deleteEntity, operation} = useSelectedOperations();
  const {switchJournal} = useJournal();
  
  const hasSelectedRow = !!selectedElement;
  const userId = appContext.user?.id ?? '';
  const checkSecJournal = !getSecBySecTask(appContext, FmsSecTask.ListJournalBillings, SecType.Read) && !userId.startsWith('@');

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      <div className='ifms-common-vertical-gap-8'>
        <UniqueLinkButton newStyle name='acPrepaidExpense-list-add-button' display="primary" link={`/${operation}/acAdvanceSales/add`} label={''} useOnlyIcon data-tooltip='New' iconMode />
        <EditIconButton2 name='acPrepaidExpense-list-edit-button' disabled={!hasSelectedRow} onClick={edit} useText/>
        <ViewIconButton2 name='acPrepaidExpense-list-view-button' disabled={!hasSelectedRow} onClick={view} useText  />
        <DeleteIconButton2 name='acPrepaidExpense-list-delete-button' disabled={!hasSelectedRow} onClick={deleteEntity} useText/>
      </div>
      {!checkSecJournal &&  <JournalIconButton onClick={() => switchJournal(JobNames.AdvanceSales, selectedElement.id)} useText/>}
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);

  const {create, edit, view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {

    } else if (unprocessedFkey === "F2") {
        create();
    } else if (unprocessedFkey === "F3") {
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }
    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F2', 'F3', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

function formatData(data:Array<AdvanceSalesListItem>) {
  return data.map(d => ({
    ...d,
  }));
}

function TrancationImpactButtonCommands({ userId } : {userId: string}) {
  return null;
}

/**
 * Concrete implementation for Advance Sales List screen
 *
 * @class AdvanceSalesListView
 * @extends {AbstractListView}
 */
class AdvanceSalesListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.AdvanceSales,
      'AdvanceSalesListItem',
      AdvanceSalesListItem.calculateSearchParameters(search,AdvanceSalesListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.AdvanceSales, 'AdvanceSalesListItem',
      AdvanceSalesListItem.calculateSearchParameters(search,AdvanceSalesListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'statusNotUseFileLocked',
        renderer: StatusNotUseFileLockedRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): ListRowRenderer[] {
    const useBranch = systemSettings?.enableBranch ?? false;
    
    return AdvanceSalesListItem.getExportCollection(useBranch).map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props:any) => <FloatRenderer {...props} decimals={2} />;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;

      if (x.dataIndex === "status") {
        colRenderer.Cell = IndicatorRenderer;
      }

      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'postdate',
      orderByAscending: false,
    };
  }

  getTableSettings(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): TableSettings {
    const tsettings =  advanceSalesList;
    const useBranch = systemSettings?.enableBranch ?? false;
    const cols = AdvanceSalesListItem.getExportCollection(useBranch);
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: AdvanceSalesListItem) {
    return row && row.id ? `/accounting/acAdvanceSales/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Advance Sales List';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.AdvanceSales} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }
  
  transactionImpactBtn(userId: string): React.ReactNode {
    return <TrancationImpactButtonCommands userId={userId} />
  }
}

export default AdvanceSalesListView;
